// Servicio de consumo para la app PWA
// Basado en consumptionService.ts de la app WEB

import axios from 'axios';
import { eventBus } from './EventBus';
import { getApiBaseUrl } from '../config';
import { getHabitacionesEspeciales } from './api';
import { getToken, getUser } from './auth';

function houseApiHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Interfaces
export interface DispositivoConConsumo {
  id: number;
  nombre: string;
  tipo: string;
  estado: boolean;
  online: boolean;
  consumo: number;
  ultimoConsumoEmitido?: number;
  habitacion_id?: number | null;
  ip?: string;
  mac?: string;
  generacion?: number;
  es_habitacion_especial?: boolean;
  lights_brightness?: number;
  cover_0_state?: string;
  cover_position?: number;
  metadata?: any;
  timer_config?: any;
  agenda_config?: any;
  artefacto_id?: number | null;
  tipo_artefacto?: string | null;
  orden?: number;
  auth_required?: boolean;
  auth_configured?: boolean;
  pin_required?: boolean;
  camera_ids?: number[];
  is_favorite?: boolean;
}

export interface HabitacionConConsumo {
  id: number;
  nombre: string;
  consumoTotal: number;
  dispositivos: number[];
}

// Mapas globales (exactamente como la app WEB)
export const mapaDispositivos: { [key: number]: DispositivoConConsumo } = {};
export const mapaHabitaciones: { [key: number]: { datos: HabitacionConConsumo, dispositivos: number[], consumoTotal: number } } = {};
export const mapaTableros: { [key: number]: { id: number, nombre: string, orden: number, habitaciones: number[] } } = {};

// CRITICO: Flag para saber si mapaDispositivos está completamente cargado
// Esto previene race conditions donde los dispositivos se renderizan antes de que mapaDispositivos esté listo
let mapaDispositivosCargado = false;

// Map para rastrear dispositivos actualizados recientemente por control
// deviceId -> timestamp de última respuesta de control exitosa
// Se usa para proteger dispositivos de ser sobrescritos por refrescarEstructuraCompleta
// durante los primeros 3 segundos después de una respuesta de control
export const recentControlResponses = new Map<number, number>();

export function isMapaDispositivosCargado(): boolean {
  return mapaDispositivosCargado;
}

// Mapas globales para grupos (similar a dispositivos/habitaciones)
// Fuente de verdad: mapaGruposById. El índice por habitación es derivado.
export const mapaGruposById: { [key: number]: any } = {};
export const mapaGruposPorHabitacion: { [key: number]: { [key: number]: any } } = {};
export const mapaGruposFavoritos: { [key: number]: { [key: number]: any } } = {};

let mapaGruposCargado = false;
let mapaGruposOwnerUserId: number | null = null;
let gruposLoadInFlight: Promise<any[]> | null = null;

export function isMapaGruposCargado(userId?: number): boolean {
  if (!mapaGruposCargado) return false;
  if (userId != null && mapaGruposOwnerUserId != null && Number(userId) !== Number(mapaGruposOwnerUserId)) {
    return false;
  }
  return true;
}

function unindexGroup(groupId: number) {
  delete mapaGruposById[groupId];
  Object.keys(mapaGruposPorHabitacion).forEach((habIdStr) => {
    const habId = Number(habIdStr);
    if (mapaGruposPorHabitacion[habId] && mapaGruposPorHabitacion[habId][groupId]) {
      delete mapaGruposPorHabitacion[habId][groupId];
    }
  });
}

function indexGroup(grupo: any) {
  if (!grupo || grupo.id == null) return;
  if (!Array.isArray(grupo.devices)) {
    grupo.devices = [];
  }
  mapaGruposById[grupo.id] = grupo;
  const roomIds = new Set<number>();
  grupo.devices.forEach((d: any) => {
    if (d && d.habitacion_id) roomIds.add(Number(d.habitacion_id));
  });
  roomIds.forEach((habId) => {
    if (!mapaGruposPorHabitacion[habId]) {
      mapaGruposPorHabitacion[habId] = {};
    }
    mapaGruposPorHabitacion[habId][grupo.id] = grupo;
  });
}

function rebuildFavoritesForOwner() {
  if (mapaGruposOwnerUserId == null) return;
  const uid = mapaGruposOwnerUserId;
  mapaGruposFavoritos[uid] = {};
  Object.values(mapaGruposById).forEach((g: any) => {
    if (g?.is_favorite) {
      mapaGruposFavoritos[uid][g.id] = g;
    }
  });
}

export function replaceAllGroups(userId: number, grupos: any[]): void {
  Object.keys(mapaGruposById).forEach((k) => delete mapaGruposById[Number(k)]);
  Object.keys(mapaGruposPorHabitacion).forEach((k) => delete mapaGruposPorHabitacion[Number(k)]);
  mapaGruposOwnerUserId = userId;
  (grupos || []).forEach((g) => indexGroup(g));
  rebuildFavoritesForOwner();
  mapaGruposCargado = true;
}

export function upsertGroupsInMap(userId: number, grupos: any[]): void {
  if (mapaGruposOwnerUserId == null) mapaGruposOwnerUserId = userId;
  (grupos || []).forEach((g) => {
    if (!g || g.id == null) return;
    unindexGroup(g.id);
    indexGroup(g);
  });
  rebuildFavoritesForOwner();
}

export function removeGroupFromMap(groupId: number): void {
  unindexGroup(groupId);
  rebuildFavoritesForOwner();
}

export function hydrateGroupDevices(grupo: any): any {
  if (!grupo || !Array.isArray(grupo.devices)) return grupo;
  const devices = grupo.devices.map((d: any) => {
    const live = mapaDispositivos[d.id];
    if (!live) return d;
    const habNombre = live.habitacion_id && mapaHabitaciones[live.habitacion_id]
      ? mapaHabitaciones[live.habitacion_id].datos?.nombre
      : d.habitacion_nombre;
    return {
      ...d,
      nombre: live.nombre ?? d.nombre,
      tipo: live.tipo ?? d.tipo,
      estado: live.estado ?? d.estado,
      consumo: live.consumo ?? d.consumo ?? 0,
      online: live.online ?? d.online,
      habitacion_id: live.habitacion_id ?? d.habitacion_id,
      habitacion_nombre: habNombre ?? d.habitacion_nombre,
      tipo_artefacto: live.tipo_artefacto ?? d.tipo_artefacto,
      artefacto_id: live.artefacto_id ?? d.artefacto_id,
      mac: live.mac ?? d.mac,
      cover_position: live.cover_position ?? d.cover_position,
    };
  });
  return { ...grupo, devices, device_count: devices.length };
}

export function listGroupsFromMap(): any[] {
  return Object.values(mapaGruposById).map(hydrateGroupDevices);
}

export function getGroupFromMap(groupId: number): any | null {
  const g = mapaGruposById[groupId];
  return g ? hydrateGroupDevices(g) : null;
}

export function listFavoriteGroupsFromMap(_userId?: number): any[] {
  return listGroupsFromMap().filter((g) => g.is_favorite);
}

export function getGroupsForRoomFromMap(habitacionId: number): any[] {
  return listGroupsFromMap().filter((g) =>
    Array.isArray(g.devices) && g.devices.some((d: any) => d.habitacion_id === habitacionId)
  );
}

export function applyGruposWsPayload(data: any): boolean {
  const user = getUser();
  if (!user?.id || !data) return false;
  if (data.user_id != null && Number(data.user_id) !== Number(user.id)) return false;

  if (data.action === 'deleted' && data.group_id != null) {
    removeGroupFromMap(Number(data.group_id));
    return true;
  }
  if (data.group && data.group.id != null) {
    upsertGroupsInMap(Number(user.id), [data.group]);
    return true;
  }
  return false;
}

// Mapa global para favoritos de dispositivos: { [userId]: { [deviceId]: FavoriteData } }
// FavoriteData: { id, device_id, orden, dispositivo }
export const mapaFavoritosDispositivos: { [key: number]: { [key: number]: any } } = {};

// Función para obtener dispositivos de una habitación
async function getDispositivosByHabitacion(habitacionId: number): Promise<DispositivoConConsumo[]> {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/habitaciones/${habitacionId}/dispositivos`, {
      headers: houseApiHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error(`Error obteniendo dispositivos de habitación ${habitacionId}:`, error);
    return [];
  }
}

// Función para obtener habitaciones especiales (usando el servicio de API)
async function getHabitacionesEspecialesLocal(): Promise<{ conexion_red?: number; consumo_total?: number }> {
  try {
    const data = await getHabitacionesEspeciales();
    return data;
  } catch (error) {
    console.error('Error obteniendo habitaciones especiales:', error);
    return {};
  }
}

// Función helper para reintentar llamadas con exponential backoff
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000,
  resourceName: string = 'recurso'
): Promise<T> {
  let lastError: any;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      const result = await fn();
      if (i > 0) {
        console.log(`✅ Éxito obteniendo ${resourceName} en intento ${i + 1}/${maxRetries}`);
      }
      return result;
    } catch (error: any) {
      lastError = error;
      
      // NO reintentar si es un error de autenticación (401) o cliente (4xx)
      // Estos errores no se resuelven reintentando y solo generan requests innecesarios
      if (error.response?.status === 401 || (error.response?.status >= 400 && error.response?.status < 500)) {
        // Lanzar inmediatamente sin reintentar
        throw error;
      }
      
      // Si es el último intento, no esperar
      if (i === maxRetries - 1) {
        break;
      }
      
      const waitTime = delay * Math.pow(2, i); // Exponential backoff
      console.warn(`⚠️ Error obteniendo ${resourceName} (intento ${i + 1}/${maxRetries}). Reintentando en ${waitTime}ms...`);
      
      // Esperar antes de reintentar
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  // Si llegamos aquí, todos los intentos fallaron
  throw lastError;
}

export async function loadUserGroupsIntoMap(userId: number, forceRefresh: boolean = false): Promise<any[]> {
  if (!forceRefresh && isMapaGruposCargado(userId)) {
    return listGroupsFromMap();
  }
  if (gruposLoadInFlight) {
    return gruposLoadInFlight;
  }
  gruposLoadInFlight = (async () => {
    try {
      const response = await retryWithBackoff(
        () => axios.get(`${getApiBaseUrl()}/usuarios/${userId}/grupos`, {
          timeout: 15000,
          headers: houseApiHeaders(),
        }),
        2,
        1000,
        'grupos'
      );
      replaceAllGroups(userId, response.data || []);
      eventBus.emit('grupos:actualizado');
      return listGroupsFromMap();
    } finally {
      gruposLoadInFlight = null;
    }
  })();
  return gruposLoadInFlight;
}

// Función para refrescar la estructura completa con llamadas paralelas y retry logic
export async function refrescarEstructuraCompleta() {
  try {
    console.log('🔄 Iniciando refresco de estructura completa...');

    const user = getUser();
    const gruposPromise = user?.id
      ? loadUserGroupsIntoMap(user.id, true).catch((err) => {
          console.warn('⚠️ No se pudieron precargar grupos:', err);
          return null;
        })
      : Promise.resolve(null);

    // Dispositivos, habitaciones, tableros y grupos en paralelo
    const [dispositivosResponse, habitacionesResponse, tablerosResponse] = await Promise.all([
      retryWithBackoff(
        () => axios.get(`${getApiBaseUrl()}/dispositivos`, { timeout: 15000, headers: houseApiHeaders() }),
        3,
        1000,
        'dispositivos'
      ),
      retryWithBackoff(
        () => axios.get(`${getApiBaseUrl()}/habitaciones`, { timeout: 15000, headers: houseApiHeaders() }),
        3,
        1000,
        'habitaciones'
      ),
      retryWithBackoff(
        () => axios.get(`${getApiBaseUrl()}/tableros`, { timeout: 15000, headers: houseApiHeaders() }),
        3,
        1000,
        'tableros'
      )
    ]);
    await gruposPromise;

    const dispositivos = dispositivosResponse.data;
    const habitaciones = habitacionesResponse.data;
    const tableros = tablerosResponse.data;

    // LOG: Verificar qué está recibiendo del backend para dispositivo 10697
    const dispositivo10697 = dispositivos.find((d: any) => d.id === 10697);
    if (dispositivo10697) {
      console.log(`[MAP-DEVICES] refrescarEstructuraCompleta() - Backend envió dispositivo 10697: pin_required=${dispositivo10697.pin_required} (tipo: ${typeof dispositivo10697.pin_required}), tipo_artefacto=${dispositivo10697.tipo_artefacto}`);
    }

    // CRITICO: NO borrar mapaDispositivos completamente para evitar que handleDeviceUpdate ignore telemetría
    // En su lugar, guardar los IDs de dispositivos existentes para eliminar solo los que ya no existen
    const existingDeviceIds = new Set(Object.keys(mapaDispositivos).map(key => Number(key)));
    
    // Limpiar solo mapas de habitaciones y tableros (no dispositivos aún)
    Object.keys(mapaHabitaciones).forEach(key => delete mapaHabitaciones[Number(key)]);
    Object.keys(mapaTableros).forEach(key => delete mapaTableros[Number(key)]);
    
    // CRITICO: Marcar mapaDispositivos como NO cargado mientras se refresca
    mapaDispositivosCargado = false;

    // Llenar mapa de dispositivos
    const PROTECTION_WINDOW = 3000; // 3 segundos de protección
    
    dispositivos.forEach((dispositivo: any) => {
      // CRITICO: Calcular now justo antes de procesar cada dispositivo
      // Esto asegura que si la telemetría llega durante el procesamiento, la comparación use el tiempo más reciente
      const now = Date.now();
      
      const deviceId = dispositivo.id;
      
      // Verificar si este dispositivo fue controlado recientemente
      const recentResponseTime = recentControlResponses.get(deviceId);
      const isRecentlyControlled = recentResponseTime && (now - recentResponseTime) < PROTECTION_WINDOW;
      
      // CRITICO: Obtener dispositivoExistente DESPUÉS de verificar protección
      // Esto asegura que si la telemetría llegó durante el procesamiento, obtengamos el estado más reciente
      const dispositivoExistente = mapaDispositivos[deviceId];
      
      // CRITICO: Preservar pin_required tal como viene del backend
      // NO usar ?? false porque convierte null/undefined a false incorrectamente
      // Si el backend envía true, debe ser true; si envía false, debe ser false; si envía null/undefined, preservar null/undefined
      const pinRequiredValue = dispositivo.pin_required !== undefined && dispositivo.pin_required !== null 
        ? Boolean(dispositivo.pin_required) 
        : dispositivo.pin_required; // Preservar null/undefined si viene así
      
      // Procesar camera_ids igual que pin_required - preservar tal como viene del backend
      let cameraIdsValue: number[] = [];
      if (dispositivo.camera_ids !== null && dispositivo.camera_ids !== undefined) {
        if (Array.isArray(dispositivo.camera_ids)) {
          cameraIdsValue = dispositivo.camera_ids;
        } else if (typeof dispositivo.camera_ids === 'string') {
          try {
            const parsed = JSON.parse(dispositivo.camera_ids);
            cameraIdsValue = Array.isArray(parsed) ? parsed : [];
          } catch {
            cameraIdsValue = [];
          }
        }
      }
      
      // Si el dispositivo fue controlado recientemente, preservar telemetría del dispositivo existente
      // La telemetría (estado, online, consumo) viene directamente del dispositivo y es más confiable
      // Solo actualizar campos de configuración desde la API
      if (isRecentlyControlled && dispositivoExistente) {
        mapaDispositivos[deviceId] = {
          ...dispositivo,
          // Preservar telemetría del dispositivo existente (viene directamente del dispositivo)
          estado: dispositivoExistente.estado !== undefined ? dispositivoExistente.estado : dispositivo.estado,
          online: dispositivoExistente.online !== undefined ? dispositivoExistente.online : dispositivo.online,
          consumo: dispositivoExistente.consumo !== undefined ? dispositivoExistente.consumo : (dispositivo.ultimo_consumo || 0),
          ultimoConsumoEmitido: dispositivoExistente.ultimoConsumoEmitido !== undefined ? dispositivoExistente.ultimoConsumoEmitido : (dispositivo.ultimo_consumo || 0),
          // Preservar campos especiales de telemetría si existen
          ...(dispositivoExistente.lights_brightness !== undefined ? { lights_brightness: dispositivoExistente.lights_brightness } : {}),
          ...(dispositivoExistente.cover_position !== undefined && dispositivo.tipo === 'PLUS2PM' ? { cover_position: dispositivoExistente.cover_position } : {}),
          // Actualizar campos de configuración desde la API
          es_habitacion_especial: dispositivo.es_habitacion_especial || false,
          auth_required: dispositivo.auth_required ?? false,
          auth_configured: dispositivo.auth_configured ?? false,
          pin_required: pinRequiredValue, // Preservar valor tal como viene del backend
          camera_ids: cameraIdsValue, // Preservar camera_ids del backend - sobrescribe el valor del spread
          // Preservar cover_position si viene del backend (puede ser undefined si cover_0_pos es None en BD)
          ...(dispositivo.tipo === 'PLUS2PM' && dispositivo.cover_position !== undefined && !dispositivoExistente.cover_position ? { cover_position: dispositivo.cover_position } : {}),
        };
      } else {
        // Dispositivo no controlado recientemente: actualizar normalmente desde API
        mapaDispositivos[deviceId] = {
          ...dispositivo,
          consumo: dispositivo.ultimo_consumo || 0,
          ultimoConsumoEmitido: dispositivo.ultimo_consumo || 0,
          es_habitacion_especial: dispositivo.es_habitacion_especial || false,
          auth_required: dispositivo.auth_required ?? false,
          auth_configured: dispositivo.auth_configured ?? false,
          pin_required: pinRequiredValue, // Preservar valor tal como viene del backend
          camera_ids: cameraIdsValue, // Preservar camera_ids del backend - sobrescribe el valor del spread
          // Preservar cover_position si viene del backend (puede ser undefined si cover_0_pos es None en BD)
          ...(dispositivo.tipo === 'PLUS2PM' && dispositivo.cover_position !== undefined ? { cover_position: dispositivo.cover_position } : {}),
        };
      }
      
      // Limpiar entradas antiguas del Map (más de 3 segundos)
      if (recentResponseTime && (now - recentResponseTime) > PROTECTION_WINDOW) {
        recentControlResponses.delete(deviceId);
      }
      
      // LOG: Validar campos críticos después de cargar en mapaDispositivos
      // NOTA: tipo_artefacto es OPCIONAL (puede ser null si el dispositivo no tiene tipo de artefacto asignado)
      const tieneTipo = mapaDispositivos[dispositivo.id].tipo !== undefined && mapaDispositivos[dispositivo.id].tipo !== null;
      const tienePinRequired = mapaDispositivos[dispositivo.id].pin_required !== undefined && mapaDispositivos[dispositivo.id].pin_required !== null;
      const tieneArtefactoId = mapaDispositivos[dispositivo.id].artefacto_id !== undefined && mapaDispositivos[dispositivo.id].artefacto_id !== null;
      // tipo_artefacto es opcional, no lo incluimos en la validación de "completo"
      const completo = tieneTipo && tienePinRequired && tieneArtefactoId;
      
      if (!completo) {
        console.log(`[MAP-DEVICES] refrescarEstructuraCompleta() - Dispositivo ID ${dispositivo.id} "${dispositivo.nombre || 'SIN NOMBRE'}" cargado en mapaDispositivos: tipo=${mapaDispositivos[dispositivo.id].tipo || 'undefined'}, pin_required=${mapaDispositivos[dispositivo.id].pin_required}, artefacto_id=${mapaDispositivos[dispositivo.id].artefacto_id || 'null'}, tipo_artefacto=${mapaDispositivos[dispositivo.id].tipo_artefacto || 'null'} ❌ INCOMPLETO`);
      }
      
      // Marcar este dispositivo como procesado (existe en la API)
      existingDeviceIds.delete(deviceId);
    });
    
    // Eliminar dispositivos que ya no existen en la API
    existingDeviceIds.forEach(deviceId => {
      delete mapaDispositivos[deviceId];
    });
    
    // CRITICO: Marcar mapaDispositivos como cargado DESPUÉS de llenarlo completamente
    mapaDispositivosCargado = true;
    console.log(`[MAP-DEVICES] refrescarEstructuraCompleta() - mapaDispositivos cargado con ${dispositivos.length} dispositivos`);

    // Llenar mapa de habitaciones
    habitaciones.forEach((habitacion: any) => {
      const dispositivosHabitacion = Object.values(mapaDispositivos)
        .filter(d => d.habitacion_id === habitacion.id);
      
      mapaHabitaciones[habitacion.id] = {
        datos: {
          ...habitacion,
          consumoTotal: dispositivosHabitacion.reduce((sum, d) => sum + d.consumo, 0),
          dispositivos: dispositivosHabitacion.map(d => d.id)
        },
        dispositivos: dispositivosHabitacion.map(d => d.id),
        consumoTotal: dispositivosHabitacion.reduce((sum, d) => sum + d.consumo, 0)
      };
    });

    // Llenar mapa de tableros
    tableros.forEach((tablero: any) => {
      mapaTableros[tablero.id] = {
        id: tablero.id,
        nombre: tablero.nombre,
        orden: tablero.orden,
        habitaciones: []
      };
    });

    // Asignar habitaciones a sus tableros correspondientes
    habitaciones.forEach((habitacion: any) => {
      if (mapaTableros[habitacion.tablero_id]) {
        mapaTableros[habitacion.tablero_id].habitaciones.push(habitacion.id);
      }
    });

    console.log('✅ Estructura completa refrescada exitosamente');
    
    // Emitir evento de estructura actualizada
    eventBus.emit('estructura:actualizada');
  } catch (error: any) {
    // Manejo mejorado de errores con información detallada
    console.error('❌ Error refrescando estructura completa:', {
      mensaje: error.message,
      codigo: error.code,
      url: error.config?.url,
      timeout: error.config?.timeout,
      intentos: 'máximo alcanzado'
    });
    
    // Emitir evento de error para que la UI pueda mostrar un mensaje al usuario
    eventBus.emit('estructura:error', {
      mensaje: 'No se pudo cargar la estructura completa',
      detalles: error.message,
      tipo: error.code === 'ECONNABORTED' ? 'timeout' : 'network'
    });
    
    // No lanzar el error para que la app siga funcionando
    // Los datos pueden llegar después por WebSocket
  }
}

// Handler para device:update
function handleDeviceUpdate(data: any) {
  if (!data || !data.id) return;
  const id = Number(data.id);
  const dispositivo = mapaDispositivos[id];
  
  // CRITICO: Si el dispositivo NO existe en mapaDispositivos, NO crear un objeto nuevo
  // Esto evita crear objetos con valores incorrectos cuando las actualizaciones de WebSocket
  // llegan antes de que refrescarEstructuraCompleta() termine de cargar los datos correctos
  // Simplemente ignorar la actualización hasta que el dispositivo esté completamente cargado
  if (!dispositivo) {
    return; // No hacer nada si el dispositivo no existe aún
  }
  
  if (dispositivo) {
    // Telemetría por defecto; si el evento trae pin_required (assign/remove PIN), aplicarlo.
    // CRITICO: NO usar ?? false para pin_required
    const pinRequiredValue = data.pin_required !== undefined && data.pin_required !== null
      ? Boolean(data.pin_required)
      : (dispositivo.pin_required !== undefined && dispositivo.pin_required !== null
        ? Boolean(dispositivo.pin_required)
        : dispositivo.pin_required);
    
    // camera_ids / auth: aplicar si vienen en el evento (Settings); si no, preservar
    const parseCameraIds = (raw: unknown): number[] | null => {
      if (raw === undefined) return null;
      if (raw === null) return [];
      if (Array.isArray(raw)) return raw.map((x) => Number(x)).filter((n) => Number.isFinite(n));
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed)
            ? parsed.map((x) => Number(x)).filter((n) => Number.isFinite(n))
            : [];
        } catch {
          return [];
        }
      }
      return [];
    };
    const fromEventCams = parseCameraIds(data.camera_ids);
    const cameraIdsValue: number[] =
      fromEventCams !== null
        ? fromEventCams
        : (parseCameraIds(dispositivo.camera_ids) ?? mapaDispositivos[id]?.camera_ids ?? []);
    const authRequiredValue = data.auth_required !== undefined && data.auth_required !== null
      ? Boolean(data.auth_required)
      : (dispositivo.auth_required ?? false);
    
    // Detectar si hay un cambio de estado significativo (de telemetría física)
    const estadoAnterior = dispositivo.estado;
    const nuevoEstado = data.estado !== undefined ? data.estado : dispositivo.estado;
    const estadoCambio = estadoAnterior !== nuevoEstado && data.estado !== undefined;
    
    // Si hay cambio de estado desde telemetría (control físico), registrar timestamp para protección
    // Esto protege el estado de ser sobrescrito por refrescarEstructuraCompleta que puede estar en curso
    if (estadoCambio) {
      recentControlResponses.set(id, Date.now());
    }
    
    // Config desde Settings (nombre / habitación / artefacto): aplicar si viene en el evento.
    // Telemetría WS suele omitirlos → se preservan. Antes se ignoraban y la UI quedaba stale
    // hasta salir/volver de Settings.
    const nextNombre = data.nombre !== undefined ? data.nombre : dispositivo.nombre;
    const nextHabitacionId = data.habitacion_id !== undefined ? data.habitacion_id : dispositivo.habitacion_id;
    const nextArtefactoId = data.artefacto_id !== undefined ? data.artefacto_id : dispositivo.artefacto_id;
    const nextTipoArtefacto = data.tipo_artefacto !== undefined ? data.tipo_artefacto : dispositivo.tipo_artefacto;

    mapaDispositivos[id] = {
      // PRESERVAR todos los campos de configuración del dispositivo original
      id: dispositivo.id,
      nombre: nextNombre,
      tipo: dispositivo.tipo, // Tipo de dispositivo (DIMMER2, PLUS2PM, etc.)
      habitacion_id: nextHabitacionId,
      ip: dispositivo.ip,
      mac: data.mac ?? dispositivo.mac, // MAC puede venir en WebSocket, pero preservar el original si no viene
      generacion: dispositivo.generacion,
      es_habitacion_especial: dispositivo.es_habitacion_especial || false,
      artefacto_id: nextArtefactoId,
      tipo_artefacto: nextTipoArtefacto,
      orden: dispositivo.orden,
      timer_config: dispositivo.timer_config,
      agenda_config: dispositivo.agenda_config,
      auth_required: authRequiredValue,
      auth_configured: data.auth_configured !== undefined
        ? Boolean(data.auth_configured)
        : (dispositivo.auth_configured ?? false),
      pin_required: pinRequiredValue,
      camera_ids: cameraIdsValue,
      is_favorite: dispositivo.is_favorite,
      // ACTUALIZAR solo campos de telemetría que vienen en WebSocket
      consumo: data.consumo ?? dispositivo.consumo ?? 0,
      ultimoConsumoEmitido: data.consumo ?? dispositivo.ultimoConsumoEmitido ?? 0,
      estado: nuevoEstado,
      online: data.online !== undefined ? data.online : dispositivo.online,
      // Campos especiales según tipo de dispositivo - PRESERVAR siempre
      // lights_brightness: preservar del dispositivo original si existe, actualizar solo si viene en WebSocket
      ...(dispositivo.lights_brightness !== undefined ? { lights_brightness: data.lights_brightness !== undefined ? data.lights_brightness : dispositivo.lights_brightness } : {}),
      // Preservar cover_position si el dispositivo es PLUS2PM (igual que lights_brightness)
      ...(dispositivo.tipo === 'PLUS2PM' ? { 
        cover_position: data.cover_position !== undefined ? data.cover_position : dispositivo.cover_position 
      } : {}),
      // metadata: preservar del dispositivo original, actualizar coverState solo si viene estado en WebSocket
      ...(dispositivo.metadata ? { 
        metadata: { 
          ...dispositivo.metadata, 
          coverState: data.estado !== undefined ? data.estado : dispositivo.metadata.coverState,
          ...(dispositivo.tipo === 'PLUS2PM' ? { 
            cover_position: data.cover_position !== undefined ? data.cover_position : dispositivo.metadata?.cover_position 
          } : {})
        } 
      } : {})
    };
    
    // LOG: Validar campos preservados después de actualización WebSocket
    // NOTA: tipo_artefacto es OPCIONAL (puede ser null si el dispositivo no tiene tipo de artefacto asignado)
    const tieneTipo = mapaDispositivos[id].tipo !== undefined && mapaDispositivos[id].tipo !== null;
    const tienePinRequired = mapaDispositivos[id].pin_required !== undefined && mapaDispositivos[id].pin_required !== null;
    const tieneArtefactoId = mapaDispositivos[id].artefacto_id !== undefined && mapaDispositivos[id].artefacto_id !== null;
    // tipo_artefacto es opcional, no lo incluimos en la validación de "completo"
    const completo = tieneTipo && tienePinRequired && tieneArtefactoId;
    
    if (!completo) {
      console.log(`[MAP-DEVICES] handleDeviceUpdate() - WebSocket actualizó dispositivo ID ${id} "${mapaDispositivos[id].nombre || 'SIN NOMBRE'}": tipo=${mapaDispositivos[id].tipo || 'undefined'}, pin_required=${mapaDispositivos[id].pin_required}, artefacto_id=${mapaDispositivos[id].artefacto_id || 'null'}, tipo_artefacto=${mapaDispositivos[id].tipo_artefacto || 'null'} ❌ INCOMPLETO`);
    }
    // Actualizar habitación
    const habitacionId = dispositivo.habitacion_id;
    if (habitacionId && mapaHabitaciones[Number(habitacionId)]) {
      const ids = mapaHabitaciones[Number(habitacionId)].dispositivos;
      mapaHabitaciones[Number(habitacionId)].consumoTotal = ids.reduce((suma: number, id: number) => suma + (mapaDispositivos[Number(id)]?.consumo || 0), 0);
      // Emitir evento de actualización de habitación
      eventBus.emit(`habitacion:${habitacionId}:actualizada`);
    }
    // Emitir evento global de dispositivo actualizado
    eventBus.emit('dispositivo:actualizado', data.id);
  }
}

// Variable global para el intervalo de refresco automático
let refreshIntervalId: NodeJS.Timeout | null = null;

// MEJORA: Suscribir listeners de telemetría INMEDIATAMENTE al cargar el módulo
// Esto asegura que los listeners estén listos desde el inicio, antes de que se monte la app
// para no perder ningún evento de telemetría que llegue durante la inicialización
eventBus.on('device:update', handleDeviceUpdate);
// Listener para el evento de refrescar estructura
eventBus.on('estructura:refrescar', refrescarEstructuraCompleta);
// Listener para forzar refresco desde backend (asignar habitación, descubrimiento, etc.)
eventBus.on('estructura:forzar_refresco', () => {
  refrescarEstructuraCompleta();
});
eventBus.on('grupos:ws', (data: any) => {
  const applied = applyGruposWsPayload(data);
  if (applied) {
    eventBus.emit('grupos:actualizado');
    return;
  }
  const user = getUser();
  if (user?.id) {
    loadUserGroupsIntoMap(user.id, true).catch(() => {});
  }
});

// Suscribirse a eventos del eventBus
export function inicializarConsumoTiempoReal(skipInitialRefresh: boolean = false) {
  // ⚡ OPTIMIZACIÓN: No refrescar estructura si ya se hizo en handleLogin
  if (!skipInitialRefresh) {
    refrescarEstructuraCompleta();
  }
  // NOTA: Los listeners ya están suscritos desde el inicio del módulo, no es necesario re-suscribirlos
  // eventBus.on('device:update', handleDeviceUpdate); // Ya suscrito arriba
  // eventBus.on('estructura:refrescar', refrescarEstructuraCompleta); // Ya suscrito arriba
  
  // Refresco automático cada 5 minutos como seguro
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
  }
  refreshIntervalId = setInterval(() => {
    refrescarEstructuraCompleta();
  }, 5 * 60 * 1000); // 5 minutos
  
  // Listener para reconexión de WebSocket (se limpia automáticamente al desmontar la app)
  // NOTA: Este listener se mantiene activo durante toda la sesión, lo cual es correcto
  // ya que queremos refrescar cuando se reconecta el WebSocket
  eventBus.on('websocket:reconnected', () => {
    refrescarEstructuraCompleta();
  });
}

// Función para limpiar el intervalo de refresco
export function limpiarRefrescoAutomatico() {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

// Función para obtener dispositivos de una habitación específica con sus consumos
export const obtenerDispositivosHabitacionConConsumo = async (habitacionId: number, forceRefresh = false): Promise<DispositivoConConsumo[]> => {
  try {
    // Obtener dispositivos de esta habitación
    const dispositivos = await getDispositivosByHabitacion(habitacionId);
    
    // Generar/actualizar consumos para cada dispositivo
    const dispositivosConConsumo = dispositivos.map((dispositivo: any) => {
      // Buscar si ya tenemos este dispositivo en memoria para mantener su consumo
      const existente = mapaDispositivos[dispositivo.id];
      
      if (existente) {
        // Si ya existe, crear un nuevo objeto con los datos actualizados
        return {
          ...existente,
          ...dispositivo,
          estado: dispositivo.estado,
          online: dispositivo.online,
          consumo: dispositivo.ultimo_consumo || 0,
          ultimoConsumoEmitido: dispositivo.ultimo_consumo || 0,
        };
      }
      
      // Usar el consumo real de la base de datos o 0 si no está disponible
      const consumo = dispositivo.ultimo_consumo || 0;
      
      return {
        ...dispositivo,
        estado: dispositivo.estado,
        online: dispositivo.online,
        consumo,
        ultimoConsumoEmitido: consumo
      };
    });

    // Guardar en memoria para esta habitación
    mapaHabitaciones[Number(habitacionId)] = {
      datos: dispositivosConConsumo[0],
      dispositivos: dispositivosConConsumo.map((d: any) => d.id),
      consumoTotal: dispositivosConConsumo.reduce((suma: number, d: any) => suma + (d.consumo || 0), 0)
    };

    return dispositivosConConsumo;
  } catch (error) {
    console.error(`Error al obtener dispositivos de habitación ${habitacionId}:`, error);
    return [];
  }
};

// Función para obtener habitaciones con consumos calculados
export const obtenerHabitacionesConConsumo = async (forceRefresh = false): Promise<HabitacionConConsumo[]> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/habitaciones`, {
      headers: houseApiHeaders(),
    });
    const habitaciones = response.data;
    
    return habitaciones.map((habitacion: any) => {
      const dispositivosHabitacion = Object.values(mapaDispositivos)
        .filter(d => d.habitacion_id === habitacion.id);
      
      return {
        ...habitacion,
        consumoTotal: dispositivosHabitacion.reduce((sum, d) => sum + d.consumo, 0),
        dispositivos: dispositivosHabitacion.map(d => d.id)
      };
    });
  } catch (error) {
    console.error('Error obteniendo habitaciones con consumo:', error);
    return [];
  }
};

// Función para obtener top 10 dispositivos por consumo
export const obtenerTop10Consumo = (): DispositivoConConsumo[] => {
  return Object.values(mapaDispositivos)
    .sort((a, b) => Math.abs(b.consumo) - Math.abs(a.consumo))
    .slice(0, 10);
};

// Función para obtener IDs del top 10
export const obtenerTop10Ids = (): number[] => {
  return obtenerTop10Consumo().map(d => d.id);
};

// Función para obtener consumo total
export const obtenerConsumoTotal = (): number => {
  return Object.values(mapaDispositivos).reduce((sum, d) => sum + d.consumo, 0);
};

// Exportar la función para obtener habitaciones especiales
export { getHabitacionesEspecialesLocal as getHabitacionesEspeciales };

// Función para obtener el color según el consumo (exactamente como en la app WEB)
export const getColorForConsumo = (consumo?: number): string => {
  // Si el consumo es undefined o null, devolver un color neutral
  if (consumo === undefined || consumo === null) return '#888888';
  
  return consumo >= 0 ? '#2391FF' : '#00ff00'; // Azul claro para consumo, verde para generación
};

// Función para formatear consumo (W o kW) - exactamente como en la app WEB
export const formatearConsumo = (watts: number): string => {
  if (Math.abs(watts) >= 1000) {
    return `${(watts / 1000).toFixed(2)} kW`;
  }
  return `${watts.toFixed(2)} W`;
};
